import React, { useRef } from "react";
import { Box, BoxProps } from "@mui/material";
import dynamic from "next/dynamic";
import { IWikifolioDetailDict } from "src/api/server/wf-detail.types";
import useViewportSpy from "src/hooks/dom/use-viewport-spy";
import { CHART_VARIANTS } from "./shared/chart.type";
import { ChartLoadingSkeleton } from "./shared/components/chart-loading-skeleton";
import { ChipSkeleton } from "./shared/components/chip-skeleton";
import { PortfolioDistributionShell } from "./shared/portfolio-distribution-shell";

interface IPortfolioDistributionSectionProps extends BoxProps {
    dict: IWikifolioDetailDict["portfolioDistribution"];
    wikifolioId: string;
}

const PortfolioDistributionSectionSkeleton = ({ dict }: { dict: IWikifolioDetailDict["portfolioDistribution"] }) => {
    return (
        <PortfolioDistributionShell
            dict={dict}
            Chip={<ChipSkeleton />}
            PieChart={<ChartLoadingSkeleton />}
            MapChart={<ChartLoadingSkeleton variant={CHART_VARIANTS.MAP} />}
        />
    );
};

const PortfolioDistributionSectionLazy = ({ dict, wikifolioId, ...props }: IPortfolioDistributionSectionProps) => {
    const PortfolioDistributionCharts = dynamic<IPortfolioDistributionSectionProps>(
        () => import("./portfolio-distribution-charts").then(component => ({ default: component.PortfolioDistributionCharts })),
        {
            loading: () => <PortfolioDistributionSectionSkeleton dict={dict} />,
            ssr: false,
        }
    );

    return <PortfolioDistributionCharts dict={dict} wikifolioId={wikifolioId} {...props} />;
};

interface IPortfolioDistributionLazyProps extends BoxProps {
    dict: IWikifolioDetailDict["portfolioDistribution"];
    wikifolioId: string;
}

/**
 * Portfolio Distribution Lazy Loader - Viewport-based Dynamic Loading
 */
export const PortfolioDistributionSection = ({ dict, wikifolioId, ...boxProps }: IPortfolioDistributionLazyProps) => {
    const sectionRef = useRef<HTMLDivElement>(null);

    const isVisible = useViewportSpy(sectionRef, {
        threshold: 0.5,
        visibleOnce: true,
    });

    return (
        <Box component="section" ref={sectionRef} {...boxProps}>
            {isVisible ? (
                <PortfolioDistributionSectionLazy dict={dict} wikifolioId={wikifolioId} />
            ) : (
                <PortfolioDistributionSectionSkeleton dict={dict} />
            )}
        </Box>
    );
};
